import * as React from "react";

import type { Deal } from "@/api/client";
import { SectionHeading } from "@/components/ui/section";
import { formatAud } from "@/lib/currency";
import { INDUSTRY_LABELS } from "@/lib/deal-labels";
import { cn } from "@/lib/utils";

/** DD/MM/YYYY (Australian) — matches DealStatusSection's "Expiry date" render
 * of the same target_close_date field, so the two surfaces agree (CHP-809). */
function formatDate(value: string | null): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

/**
 * The shared "The Deal" body — Client / Opportunity / Proposed Solution —
 * used by both the registration review (CHP-223) and the extension review
 * (CHP-233). The extension form prepends its own "Channel Partner" sub-section
 * before these; the registration form renders these alone.
 *
 * `showDealId` (default true) gates the Opportunity "Deal ID" field. The
 * standalone BDM/PO detail view already shows the Deal ID in its header
 * subtitle, so it passes `false` to avoid rendering it twice (CHP-806).
 *
 * `showOwner` (default false, CHP-1034) gates the Opportunity "Deal owner"
 * field — opt-in from the partner detail only; the manager hosts already show
 * the owner via ChannelPartnerSection.
 */
export function DealProfileSections({
  deal,
  showDealId = true,
  showOwner = false,
}: {
  deal: Deal;
  showDealId?: boolean;
  showOwner?: boolean;
}) {
  return (
    <>
      <section>
        <SectionHeading className="mb-3">The Client</SectionHeading>
        <dl className="grid gap-3 sm:grid-cols-2">
          <Field label="Name" value={deal.client_name} />
          {deal.client_abn ? <Field label="ABN" value={deal.client_abn} /> : null}
          {deal.industry_vertical ? (
            <Field label="Industry" value={INDUSTRY_LABELS[deal.industry_vertical]} />
          ) : null}
          {deal.client_lead_contact ? (
            <Field label="Lead Contact" value={deal.client_lead_contact} />
          ) : null}
        </dl>
      </section>

      <section>
        <SectionHeading className="mb-3">The Opportunity</SectionHeading>
        <dl className="grid gap-3 sm:grid-cols-2">
          {deal.total_contract_value != null ? (
            <Field label="Value" value={formatAud(deal.total_contract_value)} />
          ) : null}
          {deal.proposed_term_months != null ? (
            <Field label="Term" value={`${deal.proposed_term_months} months`} />
          ) : null}
          {deal.target_close_date ? (
            <Field label="Closing Date" value={formatDate(deal.target_close_date)} />
          ) : null}
          {showDealId && deal.deal_id_display ? (
            <Field label="Deal ID" value={deal.deal_id_display} />
          ) : null}
          {/* CHP-1034: the BDM/PO attached to the deal, in the documented
              "Name (BDM)" convention. Opt-in (showOwner) because the manager
              hosts already render this via ChannelPartnerSection's "Deal
              Owner" field — only the partner detail (which has no Channel
              Partner section) asks for it. lead_bdm_name is enriched by the
              detail endpoint only, so list-fed renders omit it regardless. */}
          {showOwner && deal.lead_bdm_name ? (
            <Field
              label="Deal owner"
              value={
                deal.lead_bdm_role
                  ? `${deal.lead_bdm_name} (${deal.lead_bdm_role})`
                  : deal.lead_bdm_name
              }
            />
          ) : null}
        </dl>
      </section>

      <section>
        <SectionHeading className="mb-3">Proposed Solution</SectionHeading>
        <dl className="grid gap-3 sm:grid-cols-2">
          {deal.service_offering ? <Field label="Service" value={deal.service_offering} /> : null}
          {deal.estimated_gpu_count != null ? (
            <Field label="GPU Count" value={String(deal.estimated_gpu_count)} />
          ) : null}
          {deal.primary_use_case ? (
            <ExpandableField
              label="Use Case"
              text={deal.primary_use_case}
              moreLabel="Read full use case"
            />
          ) : null}
          {deal.infrastructure_notes ? (
            <ExpandableField
              label="Infrastructure Notes"
              text={deal.infrastructure_notes}
              moreLabel="Read full notes"
            />
          ) : null}
        </dl>
      </section>
    </>
  );
}

export function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-sm font-medium">{label}</dt>
      <dd className="text-sm text-muted-foreground">{value}</dd>
    </div>
  );
}

/** A long free-text field shown truncated to two lines with a "Read full"
 * toggle (CHP-223 — use case + infrastructure notes). */
export function ExpandableField({
  label,
  text,
  moreLabel,
}: {
  label: string;
  text: string;
  moreLabel: string;
}) {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <div className="sm:col-span-2">
      <dt className="text-sm font-medium">{label}</dt>
      <dd
        className={cn(
          "mt-1 whitespace-pre-wrap text-sm text-muted-foreground",
          !expanded && "line-clamp-2",
        )}
      >
        {text}
      </dd>
      <button
        type="button"
        onClick={() => setExpanded((prev) => !prev)}
        className="mt-1 text-xs font-medium text-primary underline-offset-4 hover:underline"
      >
        {expanded ? "Show less" : moreLabel}
      </button>
    </div>
  );
}
